import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import BlogCard from "./BlogCard";
import CommentModal from "./CommentModal";
import "./BlogListFilters.css";

const API_HOST = process.env.REACT_APP_API_URL;

const BlogsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const authToken = sessionStorage.getItem("authToken");
  const userType = sessionStorage.getItem("userType"); // Detect user role
  
  // Blog data
  const [blogs, setBlogs] = useState([]);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters - Initialize from URL params
  const [visibility, setVisibility] = useState(searchParams.get('visibility') || "all");
  const [creator, setCreator] = useState(searchParams.get('creator') || "");
  const [category, setCategory] = useState(searchParams.get('category') || "");
  const [search, setSearch] = useState(searchParams.get('search') || "");
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || "desc");

  // Available options for dropdowns
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);

  // Comment modal state
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedUrlHeading, setSelectedUrlHeading] = useState(null);
  
  // Track if this is initial load to set page from URL
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Initialize page from URL on mount
  useEffect(() => {
    if (isInitialLoad) {
      const pageParam = searchParams.get('page');
      if (pageParam) {
        const pageNum = parseInt(pageParam);
        if (!isNaN(pageNum) && pageNum >= 1) {
          setCurrentPage(pageNum);
        }
      }
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, searchParams]);

  // Update URL when filters or page changes
  useEffect(() => {
    if (!isInitialLoad) {
      const params = new URLSearchParams();
      
      if (visibility && visibility !== "all") params.set('visibility', visibility);
      if (creator) params.set('creator', creator);
      if (category) params.set('category', category);
      if (search) params.set('search', search);
      if (sortOrder !== "desc") params.set('sortOrder', sortOrder);
      if (currentPage > 1) params.set('page', currentPage.toString());
      
      setSearchParams(params, { replace: true });
    }
  }, [visibility, creator, category, search, sortOrder, currentPage, isInitialLoad, setSearchParams]);

  useEffect(() => {
    if (!isInitialLoad) {
      fetchBlogs();
    }
  }, [currentPage, visibility, creator, category, search, sortOrder, isInitialLoad]);

  // Fetch all authors and categories once on mount for filter dropdowns
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch all blogs without filters to get complete author/category lists
        const { data } = await axios.get(
          `${API_HOST}blogs/showAdminBlogs`,
          { 
            headers: { "auth-token": authToken }, 
            params: { limit: 10000 } // Get all blogs
          }
        );

        if (data.blogs && data.blogs.length > 0) {
          const uniqueAuthors = [...new Set(data.blogs.map(b => b.creator))].filter(Boolean);
          const uniqueCategories = [...new Set(data.blogs.flatMap(b => b.categories || []))].filter(Boolean);
          setAuthors(uniqueAuthors);
          setCategories(uniqueCategories);
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchFilterOptions();
  }, []); // Only run once on mount

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 100,
      };

      if (visibility && visibility !== "all") params.visibility = visibility;
      if (creator) params.creator = creator;
      if (category) params.category = category;
      if (search) params.search = search;
      params.sortBy = "date";
      params.sortOrder = sortOrder;

      const { data } = await axios.get(
        `${API_HOST}blogs/showAdminBlogs`,
        { headers: { "auth-token": authToken }, params }
      );

      setBlogs(data.blogs || []);
      setTotalBlogs(data.totalBlogs || 0);
      setTotalPages(data.totalPages || 1);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setLoading(false);
    }
  };

  const handleOpenComments = (urlHeading) => {
    setSelectedUrlHeading(urlHeading);
    setShowCommentModal(true);
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    
    try {
      await axios.delete(`${API_HOST}blogs/deleteBlog/${id}`, {
        headers: { "auth-token": authToken },
      });
      fetchBlogs(); // Refresh list
    } catch (error) {
      console.error("Error deleting blog:", error);
      alert("Failed to delete blog");
    }
  };

  const handleClearFilters = () => {
    setVisibility("all");
    setCreator("");
    setCategory("");
    setSearch("");
    setSortOrder("desc");
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const PaginationControls = () => (
    <div className="pagination-controls">
      <div className="pagination-info">
        Showing {blogs.length > 0 ? ((currentPage - 1) * 100 + 1) : 0}-{Math.min(currentPage * 100, totalBlogs)} of {totalBlogs} blogs
      </div>
      <div className="pagination-buttons">
        <button 
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="page-btn"
        >
          ◄ Previous
        </button>
        <span className="page-indicator">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="page-btn"
        >
          Next ►
        </button>
        <input
          type="number"
          min="1"
          max={totalPages}
          value={currentPage}
          onChange={(e) => {
            const page = parseInt(e.target.value);
            if (page) goToPage(page);
          }}
          className="page-jump-input"
          placeholder="Jump to..."
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="blog-list-container">
        {/* Filter Controls */}
        <div className="filter-controls">
          <div className="filter-row">
            <input
              type="text"
              placeholder="🔍 Search by title..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />

            <select
              value={visibility}
              onChange={(e) => {
                setVisibility(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All (Public & Private)</option>
              <option value="public">Public Only</option>
              <option value="private">Private Only</option>
            </select>

            <select
              value={creator}
              onChange={(e) => {
                setCreator(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="">All Authors</option>
              {authors.map((author, idx) => (
                <option key={idx} value={author}>{author}</option>
              ))}
            </select>

            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="">All Categories</option>
              {categories.map((cat, idx) => (
                <option key={idx} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>

            <button onClick={handleClearFilters} className="clear-filters-btn">
              Clear Filters
            </button>
          </div>
        </div>

        {/* Top Pagination */}
        <PaginationControls />

        {/* Blog Grid */}
        {loading ? (
          <div className="loading-state">Loading blogs...</div>
        ) : blogs.length === 0 ? (
          <div className="empty-state">
            <p>No blogs found matching your filters. Try adjusting your search criteria.</p>
          </div>
        ) : (
          <div className="blogs-grid">
            {blogs.map((blog) => (
              <BlogCard
                key={blog._id}
                content={blog}
                onEdit={() => navigate(`/${userType === 'blogHead' ? 'blogHead' : 'admin'}/blogs/edit/${blog._id}`)}
                onDelete={() => handleDeleteBlog(blog._id)}
                onOpenComments={() => handleOpenComments(blog.urlHeading)}
              />
            ))}
          </div>
        )}

        {/* Bottom Pagination */}
        {!loading && blogs.length > 0 && <PaginationControls />}

        {/* Comment Modal */}
        {showCommentModal && (
          <CommentModal
            urlHeading={selectedUrlHeading}
            show={showCommentModal}
            handleClose={() => setShowCommentModal(false)}
          />
        )}
      </div>
    </>
  );
};

export default BlogsList;
